import {
	type AboutPage,
	type Organization,
	type WebSite,
	type WithContext,
	type SoftwareApplication,
	type WebPage,
	type Service,
	type FAQPage,
} from 'schema-dts'
import { mediaAsset } from '#app/utils/media.ts'

//TODO: Use wherever else like index
// Add your meta description
export const description = `Boost your dealership's sales with DealerProEdge's innovative website platform. Transform browsing into buying with our industry-leading digital solutions.` // Add compelling meta description about DealerProEdge

//TODO: Make dynamic schemas that you can where you repeat using data in other pages

// Organization Schema
export const organizationSchema: WithContext<Organization> = {
	'@context': 'https://schema.org',
	'@type': 'Organization',
	name: 'DealerProEdge',
	// Using both relevant NAICS codes for software and automotive
	naics: ['51', '518', '44', '441', '4411', '44111', '441110'],
	url: 'https://dealerproedge.com',
	logo: mediaAsset('/favicons/512x512.png'),
	description: `Boost your dealership's sales with DealerProEdge's innovative website platform. Transform browsing into buying with our industry-leading digital solutions.`,
	// TODO: Add later maybe?
	// address: {
	// 	'@type': 'PostalAddress',
	// 	addressLocality: '[CITY]', // Add city
	// 	addressRegion: '[STATE]', // Add state
	// 	postalCode: '[ZIP]', // Add zip
	// 	streetAddress: '[STREET_ADDRESS]', // Add street address
	// 	addressCountry: {
	// 		'@type': 'Country',
	// 		name: 'United States',
	// 	},
	// },
	sameAs: [
		'https://www.linkedin.com/company/dealer-pro-edge',
		'https://x.com/dealerproedge',
		'https://www.g2.com/products/dealer-pro-edge/reviews',
	],
	aggregateRating: {
		'@type': 'AggregateRating',
		ratingValue: 5,
		reviewCount: 6,
		bestRating: 5,
		worstRating: 1,
	},
}

// Website Schema
export const webSiteSchema: WithContext<WebSite> = {
	'@context': 'https://schema.org',
	'@type': 'WebSite',
	name: 'DealerProEdge',
	url: 'https://dealerproedge.com/',
	// TODO: Add to later whichever needed
	// potentialAction: {
	// 	'@type': 'SearchAction',
	// 	target: {
	// 		'@type': 'EntryPoint',
	// 		urlTemplate: 'https://dealerproedge.com/search?q={search_term_string}',
	// 	},
	// 	'query-input': 'required name=search_term_string',
	// },
}

//TODO: Finish later when pricing section is up
// Software Application Schema (Recommended for SaaS products)
// export const softwareSchema: WithContext<SoftwareApplication> = {
// 	'@context': 'https://schema.org',
// 	'@type': 'SoftwareApplication',
// 	name: 'DealerProEdge Platform',
// 	applicationCategory: 'BusinessApplication',
// 	operatingSystem: 'Web',
// 	offers: {
// 		'@type': 'Offer',
// 		price: '[PRICE]', // Add pricing if public
// 		priceCurrency: 'USD',
// 	},
// 	description: '[DETAILED_PRODUCT_DESCRIPTION]', // Add detailed product description
// }

// WebPage Schema (for homepage)
export function createWebPageSchema({
	name,
	description,
	type = 'WebPage',
	url,
}: {
	name: string
	description: string
	type?: 'AboutPage' | 'WebPage'
	url: string
}): WithContext<AboutPage | WebPage> {
	return {
		'@context': 'https://schema.org',
		'@type': type,
		name,
		description,
		url,
		inLanguage: 'en-US',
	}
}

export const webPageSchema: WithContext<WebPage> = createWebPageSchema({
	name: 'DealerProEdge | Advanced Automotive Dealership Solutions',
	description,
	url: 'https://dealerproedge.com/',
})

// SEO Services Schema

export const seoServicesSchema: WithContext<Service> = {
	'@context': 'https://schema.org',
	'@type': 'Service',
	name: 'Automotive SEO Services',
	serviceType: 'Search Engine Optimization',
	description:
		'Industry-leading SEO solutions that put automotive dealerships at the top of search results. Our data-driven strategies increase visibility, generate qualified leads, and drive real showroom traffic.',
	provider: {
		'@type': 'Organization',
		name: 'DealerProEdge',
	},
	offers: [
		{
			'@type': 'Offer',
			name: 'Starter SEO',
			description:
				'Foundational SEO services designed to establish local visibility and search engine trust for dealerships.',
			availability: 'https://schema.org/InStock',
		},
		{
			'@type': 'Offer',
			name: 'Advanced SEO',
			description:
				'Growth-focused SEO strategies including backlink development, content expansion, and local optimization.',
			availability: 'https://schema.org/InStock',
		},
		{
			'@type': 'Offer',
			name: 'Elite SEO',
			description:
				'High-performance SEO solution for competitive markets with advanced reporting, optimization, and direct analyst access.',
			availability: 'https://schema.org/InStock',
		},
	],
	areaServed: {
		'@type': 'Country',
		name: 'United States',
	},
	audience: {
		'@type': 'Audience',
		audienceType: 'Automotive Dealerships',
	},
	availableChannel: {
		'@type': 'ServiceChannel',
		serviceLocation: {
			'@type': 'Place',
			name: 'Remote / Digital Service',
		},
	},
}

// Dealer Website Platform Schema
export const dealerWebsiteSchema: WithContext<SoftwareApplication> = {
	'@context': 'https://schema.org',
	'@type': 'SoftwareApplication',
	name: 'DealerProEdge Website Platform',
	applicationCategory: 'BusinessApplication',
	operatingSystem: 'Web',
	description:
		'Transform your dealership with modern, mobile-first websites built to convert. Powerful inventory management, lead generation tools, and seamless CRM integration to help you sell more cars.',
	featureList: [
		'EdgeCMS Website Platform',
		'ADA-Compliant, Responsive Design',
		'Dynamic Vehicle Search Filters',
		'Inventory Management',
		'Multi-Location Inventory Sync',
		'Local SEO Optimization',
		'Analytics Reporting',
		'CRM Integration',
	],
	softwareRequirements: 'Web Browser',
	softwareVersion: '2025',
}

// Automotive Dealer Industry Page Schema
export const automotiveDealerWebsiteSchema: WithContext<SoftwareApplication> = {
	'@context': 'https://schema.org',
	'@type': 'SoftwareApplication',
	name: 'DealerProEdge Automotive Dealer Websites',
	applicationCategory: 'BusinessApplication',
	operatingSystem: 'Web',
	description:
		'Mobile-first, high-performance websites built for car dealerships, with inventory management, local SEO, and lead capture tools designed to turn shoppers into buyers.',
	featureList: [
		'Mobile-First Responsive Design',
		'Core Web Vitals Performance',
		'Multi-Location Inventory Sync',
		'Local SEO Optimization',
		'No-Code Content Editor',
		'Lead Capture and Analytics',
		'Secure Online Payments',
		'Financing and Pre-Qualification Tools',
	],
	softwareRequirements: 'Web Browser',
	softwareVersion: '2025',
}

// Powersports & Marine Dealer Industry Page Schema
export const powersportsMarineWebsiteSchema: WithContext<SoftwareApplication> =
	{
		'@context': 'https://schema.org',
		'@type': 'SoftwareApplication',
		name: 'DealerProEdge Powersports & Marine Dealer Websites',
		applicationCategory: 'BusinessApplication',
		operatingSystem: 'Web',
		description:
			'Fast, mobile-first websites built for powersports and marine dealerships, with inventory photo tooling, technical SEO, and reliable uptime support.',
		featureList: [
			'Mobile-First Responsive Design',
			'Technical SEO and AI Overview Optimization',
			'Inventory Photo Management and Watermarking',
			'Reusable Content and Offers',
			'No-Code Content Editor',
			'Uptime SLA and Live Support',
			'Local SEO Optimization',
			'Lead Capture and Analytics',
		],
		softwareRequirements: 'Web Browser',
		softwareVersion: '2025',
	}

// Motorcycle Dealer Industry Page Schema
export const motorcycleDealerWebsiteSchema: WithContext<SoftwareApplication> = {
	'@context': 'https://schema.org',
	'@type': 'SoftwareApplication',
	name: 'DealerProEdge Motorcycle Dealer Websites',
	applicationCategory: 'BusinessApplication',
	operatingSystem: 'Web',
	description:
		'Mobile-first websites built for motorcycle dealerships, with model and spec browsing, financing pre-qualification, and local SEO built for dealership-locator searches.',
	featureList: [
		'Mobile-First Responsive Design',
		'Model and Spec Browsing',
		'Financing and Pre-Qualification Tools',
		'Local SEO for Dealership Locator Searches',
		'No-Code Content Editor',
		'Lead Capture and Analytics',
		'Core Web Vitals Performance',
		'Multi-Location Inventory Sync',
	],
	softwareRequirements: 'Web Browser',
	softwareVersion: '2025',
}

// FAQ Schema — generated dynamically from on-page FAQ data
export function createFaqSchema(
	faqs: { question: string; answer: string }[],
): WithContext<FAQPage> {
	return {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		mainEntity: faqs.map((faq) => ({
			'@type': 'Question' as const,
			name: faq.question,
			acceptedAnswer: {
				'@type': 'Answer' as const,
				text: faq.answer,
			},
		})),
	}
}
